import React, { useEffect, useRef, useState } from 'react'
import axios from 'axios'
import { useHistory } from 'react-router-dom'
import { get } from 'lodash'
import { Plus, Search, ChevronLeft, ChevronRight } from 'lucide-react'

import ApplicationLayout from '../leevo_ui/ApplicationLayout/ApplicationLayout'
import LoadingSpinner from '../leevo_ui/LoadingSpinner/LoadingSpinner'
import StudentDrawer from '../leevo_ui/StudentDrawer/StudentDrawer'
import Avatar from '../leevo_ui/Avatar/Avatar'
import { requestCreator } from '../../utilities/requests'
import toast from '../../utilities/toast'
import useFacility from '../../utilities/hooks/useFacility'
import useAuth from '../../utilities/hooks/useAuth'
import useDebounce from '../../utilities/hooks/useDebounce'
import { Button } from '../ui/button'
import { Badge } from '../ui/badge'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select'
import {
  ScrollableTableContainer,
  ScrollableTable,
  ScrollableTableHeader,
  ScrollableTableBody,
  ScrollableTableHead,
  ScrollableTableRow,
  ScrollableTableCell
} from '../ui/scrollable-table'

const { get: getStudents, cancel: cancelStudentsRequest } = requestCreator()
const PER_PAGE = 50
const REGISTRATION_FILTERS = [
  { value: 'all', label: 'All' },
  { value: 'current_enrolled', label: 'Current Enrolled' },
  { value: 'future_enrolled', label: 'Future Enrolled' },
  { value: 'unenrolled', label: 'Unenrolled' }
]

function FacilitiesStudentsPage() {
  const { facility, isLoading: isFacilityLoading } = useFacility()
  const { currentUser } = useAuth()
  const { replace: redirect, push: navigate } = useHistory()
  const [isLoading, setIsLoading] = useState(true)
  const [isFetching, setIsFetching] = useState(false)
  const hasLoadedOnceRef = useRef(false)
  const [students, setStudents] = useState([])
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedStudentId, setSelectedStudentId] = useState(null)
  const [isDrawerOpen, setIsDrawerOpen] = useState(false)
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [registrationFilter, setRegistrationFilter] = useState('all')
  const [studentCounts, setStudentCounts] = useState({
    totalStudents: 0,
    currentEnrolledCount: 0,
    futureEnrolledCount: 0,
    unregisteredCount: 0
  })

  const debouncedSearch = useDebounce(searchQuery, 300)
  const facilitiesUser = currentUser?.facilitiesUsers?.find(
    (entry) => String(entry?.facility?.id) === String(facility?.id)
  )
  const isFacilityCoach = Boolean(facilitiesUser?.coach)
  const canManageStudentDetails = Boolean(currentUser?.superuser || facilitiesUser?.admin || isFacilityCoach)
  const canDeleteStudent = Boolean(currentUser?.superuser || facilitiesUser?.admin)

  function loadStudents() {
    return getStudents(`/api/facilities/${facility.id}/students`, {
      params: {
        page,
        per_page: PER_PAGE,
        search: debouncedSearch || undefined,
        registration_filter: registrationFilter
      }
    }).then(({ records, meta }) => {
      setStudents(records || [])
      setTotalPages(meta?.total_pages || 1)
      setStudentCounts({
        totalStudents: meta?.total_students || 0,
        currentEnrolledCount: meta?.current_enrolled_count || 0,
        futureEnrolledCount: meta?.future_enrolled_count || 0,
        unregisteredCount: meta?.unregistered_count || 0
      })
    })
  }

  useEffect(() => {
    if (isFacilityLoading || !facility?.id) return

    setIsFetching(true)
    if (!hasLoadedOnceRef.current) {
      setIsLoading(true)
    }
    loadStudents()
      .catch((error) => {
        if (!axios.isCancel(error)) {
          if (get(error, 'response.status') === 401) {
            redirect('/login')
          } else {
            redirect('/500')
          }
        }
      })
      .finally(() => {
        setIsLoading(false)
        setIsFetching(false)
        hasLoadedOnceRef.current = true
      })

    return cancelStudentsRequest
  }, [facility?.id, isFacilityLoading, page, debouncedSearch, redirect, registrationFilter])

  function countForFilter(filter) {
    if (filter === 'current_enrolled') return studentCounts.currentEnrolledCount
    if (filter === 'future_enrolled') return studentCounts.futureEnrolledCount
    if (filter === 'unenrolled') return studentCounts.unregisteredCount
    return studentCounts.totalStudents
  }

  function handleRegistrationFilterChange(nextFilter) {
    setRegistrationFilter(nextFilter)
    setPage(1)
  }

  function registrationFilterLabel(filter) {
    const item = REGISTRATION_FILTERS.find((entry) => entry.value === filter)
    if (!item) return 'All'
    return `${item.label} (${countForFilter(item.value)})`
  }

  function formatBirthdate(value) {
    if (!value) return '-'
    const [datePart] = String(value).split('T')
    const [year, month, day] = datePart.split('-').map(Number)
    if (!year || !month || !day) return '-'
    const date = new Date(year, month - 1, day)
    if (Number.isNaN(date.getTime())) return '-'
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
  }

  function formatGuardianPhone(value) {
    if (!value) return '-'
    const digits = String(value).replace(/\D/g, '')
    if (digits.length === 11 && digits.startsWith('1')) {
      const area = digits.slice(1, 4)
      const prefix = digits.slice(4, 7)
      const line = digits.slice(7, 11)
      return `+1 (${area}) ${prefix}-${line}`
    }
    if (digits.length === 10) {
      const area = digits.slice(0, 3)
      const prefix = digits.slice(3, 6)
      const line = digits.slice(6, 10)
      return `(${area}) ${prefix}-${line}`
    }
    return value
  }

  return (
    <ApplicationLayout data-cy="students-index">
      <StudentDrawer
        open={isDrawerOpen}
        onClose={() => {
          setIsDrawerOpen(false)
          setSelectedStudentId(null)
        }}
        studentId={selectedStudentId}
        facilityId={facility?.id}
        onUpdate={loadStudents}
        onDelete={loadStudents}
        onActionSuccess={(message) => toast.success(message)}
        onActionError={(message) => toast.error(message)}
        canManageStudentDetails={canManageStudentDetails}
        canDeleteStudent={canDeleteStudent}
        facilityTimeZone={facility?.timeZone}
      />

      <div className="px-4 sm:px-6 lg:px-8 pt-6">
        <div className="flex items-center justify-between gap-3">
          <h1 className="text-2xl font-semibold text-gray-900">Students</h1>
          <Button onClick={() => navigate(`/facilities/${facility.id}/students/new`)}>
            <Plus className="h-4 w-4 mr-1" />
            Add Student
          </Button>
        </div>

        <div className="relative max-w-xl mt-5">
          <Search className="h-4 w-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            value={searchQuery}
            onChange={(event) => {
              setSearchQuery(event.target.value)
              setPage(1)
            }}
            placeholder="Search students by name or student email"
            className="w-full rounded-md border border-gray-300 bg-white py-2 pl-9 pr-3 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-purple-600"
          />
        </div>
        <div className="mt-4 sm:hidden">
          <Select value={registrationFilter} onValueChange={handleRegistrationFilterChange}>
            <SelectTrigger className="h-11 text-base">
              <SelectValue>{registrationFilterLabel(registrationFilter)}</SelectValue>
            </SelectTrigger>
            <SelectContent>
              {REGISTRATION_FILTERS.map((filter) => (
                <SelectItem key={filter.value} value={filter.value} className="py-2 text-base">
                  {filter.label} ({countForFilter(filter.value)})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="mt-4 hidden flex-wrap gap-2 sm:flex">
          {REGISTRATION_FILTERS.map((filter) => {
            const isActive = registrationFilter === filter.value
            return (
              <Button
                key={filter.value}
                type="button"
                size="sm"
                variant={isActive ? 'default' : 'outline'}
                onClick={() => handleRegistrationFilterChange(filter.value)}
              >
                {filter.label} ({countForFilter(filter.value)})
              </Button>
            )
          })}
        </div>
        {isFetching && hasLoadedOnceRef.current && <p className="mt-2 text-sm text-gray-500">Updating results...</p>}
        {isFacilityCoach && (
          <p className="mt-2 text-sm text-gray-600">You can view and edit students enrolled in classes you coach.</p>
        )}

        <div className="mt-5">
          <ScrollableTableContainer className="hidden lg:block" height={650}>
            <ScrollableTable>
              <ScrollableTableHeader>
                <ScrollableTableRow>
                  <ScrollableTableHead>Student</ScrollableTableHead>
                  <ScrollableTableHead>Birthdate</ScrollableTableHead>
                  <ScrollableTableHead>Age</ScrollableTableHead>
                  <ScrollableTableHead className="w-[220px]">Guardian Email</ScrollableTableHead>
                  <ScrollableTableHead>Guardian Phone</ScrollableTableHead>
                  <ScrollableTableHead>Skill Level</ScrollableTableHead>
                  <ScrollableTableHead className="w-[240px]">Current/Upcoming Class</ScrollableTableHead>
                  <ScrollableTableHead className="text-right">Actions</ScrollableTableHead>
                </ScrollableTableRow>
              </ScrollableTableHeader>
              <ScrollableTableBody>
                {isLoading && students.length === 0 && (
                  <ScrollableTableRow>
                    <ScrollableTableCell colSpan={8} className="py-10">
                      <LoadingSpinner size="1/5" className="flex items-center justify-center" />
                    </ScrollableTableCell>
                  </ScrollableTableRow>
                )}
                {students.map((student) => (
                  <ScrollableTableRow
                    key={student.id}
                    className="cursor-pointer"
                    onClick={() => {
                      setSelectedStudentId(student.id)
                      setIsDrawerOpen(true)
                    }}
                  >
                    <ScrollableTableCell>
                      <div className="flex items-center gap-3">
                        <Avatar
                          fullName={student.fullName || student.preferredName}
                          cloudinaryPhotoPublicId={student.cloudinaryPhotoPublicId}
                          userId={student.id}
                          isStudent
                          size={10}
                        />
                        <div>
                          <p className="font-medium text-gray-900">{student.preferredName || student.fullName}</p>
                          {student.preferredName && student.fullName !== student.preferredName && (
                            <p className="text-xs text-gray-500">{student.fullName}</p>
                          )}
                        </div>
                      </div>
                    </ScrollableTableCell>
                    <ScrollableTableCell className="text-gray-700">
                      {formatBirthdate(student.birthdate)}
                    </ScrollableTableCell>
                    <ScrollableTableCell className="text-gray-700">{student.age ?? '-'}</ScrollableTableCell>
                    <ScrollableTableCell className="text-gray-900">
                      <span className="block max-w-[220px] truncate">{student.guardianEmail || '-'}</span>
                    </ScrollableTableCell>
                    <ScrollableTableCell className="text-gray-900">
                      {formatGuardianPhone(student.guardianPhoneNumber)}
                    </ScrollableTableCell>
                    <ScrollableTableCell>
                      <Badge variant="secondary">{student.currentSkillLevelName || 'Unassigned'}</Badge>
                    </ScrollableTableCell>
                    <ScrollableTableCell className="text-gray-900">
                      {student.currentClassLabel ? (
                        <div className="min-w-0 space-y-1">
                          <div className="flex items-center gap-1 min-w-0">
                            <span className="block max-w-[180px] truncate">{student.currentClassLabel}</span>
                            {student.additionalCurrentClassesCount > 0 && (
                              <span className="shrink-0">{`+${student.additionalCurrentClassesCount} more`}</span>
                            )}
                          </div>
                          {student.currentClassPackageLabel && (
                            <div className="flex items-center gap-1 min-w-0 text-xs text-gray-500">
                              <span className="block max-w-[180px] truncate">{student.currentClassPackageLabel}</span>
                              {student.additionalCurrentClassPackagesCount > 0 && (
                                <span className="shrink-0">{`+${student.additionalCurrentClassPackagesCount} more`}</span>
                              )}
                            </div>
                          )}
                        </div>
                      ) : (
                        '-'
                      )}
                    </ScrollableTableCell>
                    <ScrollableTableCell>
                      <div className="flex justify-end">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={(event) => {
                            event.stopPropagation()
                            setSelectedStudentId(student.id)
                            setIsDrawerOpen(true)
                          }}
                        >
                          Edit
                        </Button>
                      </div>
                    </ScrollableTableCell>
                  </ScrollableTableRow>
                ))}
                {students.length === 0 && (
                  <ScrollableTableRow>
                    <ScrollableTableCell colSpan={8} className="py-10 text-center text-sm text-gray-600">
                      No students found.
                    </ScrollableTableCell>
                  </ScrollableTableRow>
                )}
              </ScrollableTableBody>
            </ScrollableTable>
          </ScrollableTableContainer>

          <div className="space-y-3 lg:hidden">
            {isLoading && students.length === 0 ? (
              <div className="rounded-lg border border-gray-200 bg-white px-6 py-10">
                <LoadingSpinner size="1/5" className="flex items-center justify-center" />
              </div>
            ) : students.length === 0 ? (
              <div className="rounded-lg border border-gray-200 bg-white px-6 py-12 text-center text-sm text-gray-600">
                No students found.
              </div>
            ) : (
              students.map((student) => (
                <div
                  key={`mobile-${student.id}`}
                  className="rounded-lg border border-gray-200 bg-white p-4"
                  onClick={() => {
                    setSelectedStudentId(student.id)
                    setIsDrawerOpen(true)
                  }}
                >
                  <div className="mb-3 flex items-center gap-3">
                    <Avatar
                      fullName={student.fullName || student.preferredName}
                      cloudinaryPhotoPublicId={student.cloudinaryPhotoPublicId}
                      userId={student.id}
                      isStudent
                      size={12}
                    />
                    <div className="min-w-0 flex-1">
                      <p className="truncate font-medium text-gray-900">{student.preferredName || student.fullName}</p>
                      {student.preferredName && student.fullName !== student.preferredName && (
                        <p className="truncate text-xs text-gray-500">{student.fullName}</p>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <p className="mb-1 text-xs font-medium uppercase text-gray-500">Birthdate</p>
                      <p className="text-sm text-gray-900">{formatBirthdate(student.birthdate)}</p>
                    </div>
                    <div>
                      <p className="mb-1 text-xs font-medium uppercase text-gray-500">Age</p>
                      <p className="text-sm text-gray-900">{student.age ?? '-'}</p>
                    </div>
                    <div>
                      <p className="mb-1 text-xs font-medium uppercase text-gray-500">Guardian Email</p>
                      <p className="truncate text-sm text-gray-900">{student.guardianEmail || '-'}</p>
                    </div>
                    <div>
                      <p className="mb-1 text-xs font-medium uppercase text-gray-500">Guardian Phone</p>
                      <p className="text-sm text-gray-900">{formatGuardianPhone(student.guardianPhoneNumber)}</p>
                    </div>
                  </div>

                  <div className="mt-3 grid grid-cols-1 gap-3">
                    <div>
                      <p className="mb-1 text-xs font-medium uppercase text-gray-500">Skill Level</p>
                      <Badge variant="secondary">{student.currentSkillLevelName || 'Unassigned'}</Badge>
                    </div>
                    <div>
                      <p className="mb-1 text-xs font-medium uppercase text-gray-500">Current/Upcoming Class</p>
                      {student.currentClassLabel ? (
                        <div className="space-y-1 min-w-0">
                          <div className="flex items-center gap-1 min-w-0">
                            <span className="truncate text-sm text-gray-900">{student.currentClassLabel}</span>
                            {student.additionalCurrentClassesCount > 0 && (
                              <span className="shrink-0 text-sm text-gray-900">{`+${student.additionalCurrentClassesCount} more`}</span>
                            )}
                          </div>
                          {student.currentClassPackageLabel && (
                            <div className="flex items-center gap-1 min-w-0">
                              <span className="truncate text-xs text-gray-500">{student.currentClassPackageLabel}</span>
                              {student.additionalCurrentClassPackagesCount > 0 && (
                                <span className="shrink-0 text-xs text-gray-500">{`+${student.additionalCurrentClassPackagesCount} more`}</span>
                              )}
                            </div>
                          )}
                        </div>
                      ) : (
                        <p className="text-sm text-gray-900">-</p>
                      )}
                    </div>
                  </div>

                  <div className="mt-4 border-t border-gray-200 pt-3">
                    <Button
                      className="w-full"
                      variant="outline"
                      size="sm"
                      onClick={(event) => {
                        event.stopPropagation()
                        setSelectedStudentId(student.id)
                        setIsDrawerOpen(true)
                      }}
                    >
                      Edit
                    </Button>
                  </div>
                </div>
              ))
            )}
          </div>

          <div className="mt-4 flex items-center justify-end gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={page <= 1}
              onClick={() => setPage((previous) => previous - 1)}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="text-sm text-gray-700">
              Page {page} of {totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              disabled={page >= totalPages}
              onClick={() => setPage((previous) => previous + 1)}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
    </ApplicationLayout>
  )
}

export default FacilitiesStudentsPage
